import { NgModule } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { CommonModule } from '@angular/common';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatDialogModule } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { MatMenuModule } from '@angular/material/menu';
import { MatRadioModule } from '@angular/material/radio';
import { ColorPickerComponent, ColorPickerDirective } from 'ngx-color-picker';
import { UtilitiesModule } from '@fi-app/utilities/utilities.module';
import { WidgetModule } from '@fi-app/widget/widget.module';
import { ComponentsModule } from '@fi-app/components/components.module';
import { DocumentTemplateModule } from '@fi-app/document-template/document-template.module';
import { DocumentItemViewComponent } from './item-view/item-view.component';
import { DocumentLayoutComponent } from './layout/layout.component';
import { DocumentLayoutViewModule } from './layout/document-layout-view.module';
import { DocumentViewCardComponent } from './document-view-card/document-view-card.component';
import { LastEditInfoComponent } from './last-edit-info/last-edit-info.component';
import { DocumentDialogAbsentContentLanguageComponent } from './dialog-absent-content-language/dialog-absent-content-language.component';
import { DocumentViewService } from './document-view-service/document-view.service';
import { FiToggleVisibilityModule } from '@fi-app/utilities/toggle-visibility/toggle-visibility.module';
import { DocumentService } from './document.service';
import { DocumentListDataService } from './document-list-data-service';
import { DocumentDialogEditConfirmSaveComponent } from './edit/confirm-save/document-confirm-save.component';
import { FiDocumentPanelRootModule } from './document-panel-root/document-panel-root.module';
import { FiDocumentVersionListModule } from './version-list/version-list.module';
import { DocumentEditService } from './document-edit.service';
import { DocumentListContainerComponent } from './document-list-container/document-list-container.component';
import { DocumentViewContainerComponent } from './document-view-container/document-view-container.component';
import { DocumentListViewComponent } from './document-list-view/document-list-view.component';
import { DocumentListViewStateComponent } from './document-list-view-state/document-list-view-state.component';
import { FiDocumentViewComponent } from './document-view/document-view.component';
import { FiTextItemLinksToPopupOpeners } from '../utilities/text-item-links-to-popup/text-item-links-to-popup.directive';
import { FiDocumentViewDialogComponent } from './document-view-dialog/document-view-dialog.component';
import { FiDocumentViewStateComponent } from './document-view-state/document-view-state.component';
import { FiDocumentLayoutModule } from './document-layout/document-layout.module';
import { UIRouterModule } from '@uirouter/angular';
import { FiDocumentStates } from '@fi-app/document/document-states';
import { FiDocumentEditSitemapHandlerComponent } from '@fi-app/document/edit-item/sitemap-handler/sitemap-handler.component';
import { FiLinkToType } from '@fi-shared/fi-text-item-helper/fi-link-to-type';
import { FiPrintDownloadModule } from '@fi-shared/fi-print-download/fi-print-download.module';
import { DocumentGeneratePreviewComponent } from '@fi-app/document/document-generate-preview/document-generate-preview.component';
import { DocumentGeneratePreviewService } from '@fi-app/document/document-generate-preview/document-generate-preview.service';
import { FiLikesModule } from '@fi-shared/fi-likes/fi-likes.module';
import { FiPageHeaderButtonGroupModule } from '@fi-shared/fi-page-header-button-group/fi-page-header-button-group.module';
import { FiItemsListModule } from '@fi-shared/fi-items-list/fi-items-list.module';
import { DocumentNavigationTabsComponent } from '@fi-app/document/document-navigation-tabs/document-navigation-tabs.component';
import { FiPageHeaderModule } from '@fi-app/components/fi-page-header/fi-page-header.module';
import { FiSiteDesignModule } from '@fi-app/fi-site-design/fi-site-design.module';
import { DocumentListComponent } from './document-list/document-list.component';
import { FiLocalSearch2Module } from '@fi-shared/fi-local-search2/fi-local-search2.module';
import { FiWrapperTableModule } from '@fi-shared/fi-wrapper-table/fi-wrapper-table.module';

const FiTextItemLinksToPopupOpenersDocumentProvider = {
    provide: FiTextItemLinksToPopupOpeners,
    useFactory: (documentViewService: DocumentViewService) => (linkToModel) => {
        if (linkToModel.Type === FiLinkToType.Document) {
            documentViewService.openDocumentViewDialog(linkToModel.Value.Id, {
                showInsert: false,
                showBackButton: false,
                Title: linkToModel.Value.Name,
            });
            return true;
        }
    },
    deps: [DocumentViewService],
    multi: true,
};

@NgModule({
    imports: [
        CommonModule,
        FormsModule,
        UtilitiesModule,
        MatRadioModule,
        MatCheckboxModule,
        MatCardModule,
        MatMenuModule,
        MatButtonModule,
        MatIconModule,
        MatRadioModule,
        MatDialogModule,
        ColorPickerComponent,
        ColorPickerDirective,
        DocumentTemplateModule,
        WidgetModule,
        ComponentsModule,
        FiToggleVisibilityModule,
        FiDocumentPanelRootModule,
        FiDocumentVersionListModule,
        FiPrintDownloadModule,
        FiDocumentLayoutModule,
        DocumentLayoutViewModule,
        UIRouterModule.forChild(FiDocumentStates),
        FiLikesModule,
        FiPageHeaderButtonGroupModule,
        FiItemsListModule,
        FiPageHeaderModule,
        FiSiteDesignModule,
        FiLocalSearch2Module,
        FiWrapperTableModule,
    ],
    declarations: [
        DocumentViewCardComponent,
        LastEditInfoComponent,
        DocumentDialogAbsentContentLanguageComponent,
        DocumentDialogEditConfirmSaveComponent,
        DocumentListContainerComponent,
        DocumentViewContainerComponent,
        DocumentListViewComponent,
        DocumentListViewStateComponent,
        DocumentNavigationTabsComponent,
        FiDocumentViewComponent,
        FiDocumentViewDialogComponent,
        FiDocumentViewStateComponent,
        DocumentGeneratePreviewComponent,
        DocumentListComponent,
    ],
    exports: [
        DocumentTemplateModule,
        DocumentLayoutViewModule,
        FiDocumentPanelRootModule,
        FiDocumentViewComponent,
        DocumentListViewComponent,
        FiDocumentLayoutModule,
        DocumentGeneratePreviewComponent,
        DocumentViewCardComponent,
        DocumentListComponent,
    ],
    providers: [
        DocumentService,
        DocumentViewService,
        DocumentListDataService,
        DocumentEditService,
        DocumentGeneratePreviewService,
        FiTextItemLinksToPopupOpenersDocumentProvider,
    ],
})
export class DocumentModule {}
